import { readString } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';

export const INDEX_COLLECTION_BLOCK_TYPE = 'index_collection' as const;
export const INDEX_PINNED_ROLE = 'index_pinned' as const;

export const INDEX_COLLECTION_DEFINITIONS = [
  {
    sectionType: 'blog_index',
    contentType: 'blog',
  },
  {
    sectionType: 'case_result_index',
    contentType: 'case_result',
  },
] as const satisfies readonly {
  sectionType: string;
  contentType: string;
}[];

export type IndexCollectionDefinition = (typeof INDEX_COLLECTION_DEFINITIONS)[number];
export type IndexCollectionSectionType = IndexCollectionDefinition['sectionType'];
export type IndexCollectionContentType = IndexCollectionDefinition['contentType'];

const SECTION_TYPE_DEFINITIONS = new Map<string, IndexCollectionDefinition>();
const CONTENT_TYPES = new Map<string, IndexCollectionDefinition>();

for (const definition of INDEX_COLLECTION_DEFINITIONS) {
  SECTION_TYPE_DEFINITIONS.set(definition.sectionType, definition);
  CONTENT_TYPES.set(definition.contentType, definition);
}

const INDEX_FILTER_ROLE = 'index_filters';

export function getIndexCollectionDefinitionBySectionType(
  sectionType: string | undefined,
): IndexCollectionDefinition | undefined {
  return typeof sectionType === 'string' ? SECTION_TYPE_DEFINITIONS.get(sectionType) : undefined;
}

export function getIndexCollectionDefinitionByContentType(
  contentType: string | undefined,
): IndexCollectionDefinition | undefined {
  return typeof contentType === 'string' ? CONTENT_TYPES.get(contentType) : undefined;
}

export function resolveIndexCollectionSectionType({
  sectionType,
  sectionTemplateCode,
}: {
  sectionType?: string;
  sectionTemplateCode?: string;
}): IndexCollectionSectionType | undefined {
  const sectionDefinition = getIndexCollectionDefinitionBySectionType(sectionType);
  if (sectionDefinition) return sectionDefinition.sectionType;

  const templateDefinition = getIndexCollectionDefinitionBySectionType(sectionTemplateCode);
  if (templateDefinition) return templateDefinition.sectionType;

  return undefined;
}

function collectBlockSubtree(blocks: PageBlock[], rootBlock: PageBlock): PageBlock[] {
  const childrenByParent = new Map<string, PageBlock[]>();

  for (const block of blocks) {
    if (!block.parentBlockId) continue;
    const siblings = childrenByParent.get(block.parentBlockId) ?? [];
    siblings.push(block);
    childrenByParent.set(block.parentBlockId, siblings);
  }

  const result: PageBlock[] = [rootBlock];
  const queue = [...(childrenByParent.get(rootBlock.id) ?? [])];

  for (let index = 0; index < queue.length; index += 1) {
    const block = queue[index];
    result.push(block);
    queue.push(...(childrenByParent.get(block.id) ?? []));
  }

  return result;
}

function isIndexCollectionRootBlock(block: Pick<PageBlock, 'blockType'>) {
  return block.blockType === INDEX_COLLECTION_BLOCK_TYPE;
}

export function isIndexPinnedCollectionBlock(
  block: Pick<PageBlock, 'blockType' | 'props'>,
): boolean {
  return (
    block.blockType === INDEX_COLLECTION_BLOCK_TYPE &&
    readString(block.props?.role) === INDEX_PINNED_ROLE
  );
}

function findIndexCollectionRoot(blocks: PageBlock[], role: 'pinned' | 'list') {
  return blocks.find((block) => {
    if (!isIndexCollectionRootBlock(block)) return false;
    const blockRole = readString(block.props?.role);
    if (role === 'pinned') return blockRole === INDEX_PINNED_ROLE;
    return blockRole !== INDEX_PINNED_ROLE;
  });
}

export function findIndexPinnedCollectionSubtree(blocks: PageBlock[]): PageBlock[] {
  const root = findIndexCollectionRoot(blocks, 'pinned');
  return root ? collectBlockSubtree(blocks, root) : [];
}

export function findIndexListCollectionSubtree(blocks: PageBlock[]): PageBlock[] {
  const root = findIndexCollectionRoot(blocks, 'list');
  return root ? collectBlockSubtree(blocks, root) : [];
}

export function resolveIndexFilterBlock(blocks: PageBlock[]): PageBlock | undefined {
  return blocks.find((block) => block.props?.role === INDEX_FILTER_ROLE);
}
